import { Menu, LogOut, X } from "lucide-react";
import { useState } from "react";
import type { ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { Badge, Button } from "@/design-system";
import { useAuth } from "@/features/auth";

import {
  SHORTPAY_NAV_LINKS,
  type AppShell as TAppShell,
} from "@/lib/navigation";

import { ShellBrand } from "./ShellBrand";

interface AppShellProps {
  shell: TAppShell;
  tenantName?: string | null;
  tenantSlug?: string | null;
  children?: ReactNode;
}

function NavRow(
  { to, label, onNavigate }: { to: string; label: string; onNavigate?: () => void },
) {
  return (
    <li>
      <NavLink
        to={to}
        end={to === "/shortpay"}
        onClick={onNavigate}
        className={({ isActive }) =>
          [
            "flex w-full min-h-[44px] items-center gap-2 rounded-md px-3 text-sm transition-fast ease-out",
            "focus-visible:outline-none focus-visible:shadow-focus",
            isActive
              ? "bg-teal/15 text-text-primary"
              : "text-text-secondary hover:bg-recessed hover:text-text-primary",
          ].join(" ")
        }
      >
        <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full bg-current" />
        {label}
      </NavLink>
    </li>
  );
}

export function AppShell({
  shell,
  tenantName,
  tenantSlug,
  children,
}: AppShellProps) {
  const auth = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="flex h-dvh min-h-0 flex-col bg-canvas text-text-primary">
      <header className="flex flex-none items-center justify-between border-b border-border-subtle bg-canvas px-4 py-3 md:px-6">
        <a
          href="#app-main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-teal focus:px-3 focus:py-2 focus:text-text-inverse"
        >
          Skip to content
        </a>
        <ShellBrand shell={shell} compact />

        <div className="hidden items-center gap-2 md:flex">
          {tenantName ? (
            <Badge variant="category" className="max-w-[18rem] truncate">
              {tenantName}
            </Badge>
          ) : null}
          {tenantSlug ? <Badge variant="neutral">{tenantSlug}</Badge> : null}
        </div>

        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="md:hidden min-h-[44px] min-w-[44px]"
          aria-expanded={menuOpen ? "true" : "false"}
          aria-controls="shortpay-shell-nav"
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)] md:grid-cols-[280px_minmax(0,1fr)]">
        <nav
          id="shortpay-shell-nav"
          aria-label="Primary"
          className={`min-h-0 overflow-y-auto border-r border-border-subtle bg-canvas px-4 py-4 md:block md:p-5 ${
            menuOpen ? "block" : "hidden"
          }`}
        >
          <ul className="space-y-1 md:space-y-2">
            {tenantName ? (
              <li className="rounded-md bg-recessed p-3 text-sm md:hidden" data-testid="shell-tenant-mobile">
                <p className="font-medium">{tenantName}</p>
                {tenantSlug ? <p className="text-text-tertiary">/{tenantSlug}</p> : null}
              </li>
            ) : null}
            {SHORTPAY_NAV_LINKS.map((link) => (
              <NavRow key={link.to} to={link.to} label={link.label} onNavigate={closeMenu} />
            ))}
          </ul>

          <button
            type="button"
            onClick={() => {
              void auth.logout();
              closeMenu();
            }}
            className="mt-6 flex min-h-[44px] w-full items-center rounded-md px-3 py-2 text-sm text-text-secondary transition-fast ease-out hover:bg-recessed hover:text-text-primary focus-visible:outline-none focus-visible:shadow-focus"
            aria-label="Sign out"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </button>
        </nav>

        <main
          id="app-main-content"
          className="min-h-0 min-w-0 overflow-y-auto p-4"
          role="main"
        >
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
